<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import SiteAyarService from '@/services/site_ayar'
import type { ISiteAyar } from '@/model/site_ayar'
import { ErrorToast } from '@/utils/popup'

const { locale, t } = useI18n()
const router = useRouter()

withDefaults(
  defineProps<{
    borderless?: boolean
    showArrow?: boolean
  }>(),
  {
    borderless: false,
    showArrow: true,
  },
)

const siteAyar = ref<ISiteAyar | null>(null)
const loading = ref(false)
const currentLocale = computed({
  get: () => locale.value,
  set: (value) => {
    locale.value = value
  },
})
const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)

const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
  loading.value = true
  const [err, ayar] = await SiteAyarService.getSiteAyar()
  loading.value = false
  if (err) {
    ErrorToast(t('error_page.error_occurred'))
    await router.push({
      path: '/error',
      query: {
        status: '500',
        message: t('error_page.error_try_again') + ' ' + err,
        force: 'true',
      },
    })
    return
  }
  siteAyar.value = ayar
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

const allLanguages = [
  { code: 'tr', flag: '🇹🇷', name: 'Türkçe', countryCode: 'TR' },
  { code: 'en', flag: '🇺🇸', name: 'English', countryCode: 'US' },
  { code: 'ky', flag: '🇰🇬', name: 'Кыргызча', countryCode: 'KG' },
  { code: 'bs', flag: '🇧🇦', name: 'Bosanski', countryCode: 'BA' },
  { code: 'fa', flag: '🇮🇷', name: 'فارسی', countryCode: 'IR' },
  { code: 'sq', flag: '🇦🇱', name: 'Shqip', countryCode: 'AL' },
  { code: 'ar', flag: '🇸🇦', name: 'العربية', countryCode: 'SA' },
  { code: 'fr', flag: '🇫🇷', name: 'Français', countryCode: 'FR' },
  { code: 'ta', flag: '🇱🇰', name: 'தமிழ்', countryCode: 'LK' },
  { code: 'nl', flag: '🇳🇱', name: 'Nederlands', countryCode: 'NL' },
  { code: 'ru', flag: '🇷🇺', name: 'Русский', countryCode: 'RU' },
  { code: 'ur', flag: '🇵🇰', name: 'اردو', countryCode: 'PK' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch', countryCode: 'DE' },
  { code: 'id', flag: '🇮🇩', name: 'Bahasa Indonesia', countryCode: 'ID' },
  { code: 'ms', flag: '🇲🇾', name: 'Bahasa Melayu', countryCode: 'MY' },
]

const languages = computed(() => {
  if (!siteAyar.value?.language_list || siteAyar.value?.language_list.length === 0) {
    return []
  }
  return allLanguages.filter((lang) => siteAyar.value?.language_list?.includes(lang.code))
})

const currentLanguage = computed(
  () => languages.value.find((lang) => lang.code === currentLocale.value) || languages.value[0],
)

const rtlLocales = ['ar', 'fa', 'ur']
const isRTL = computed(() => rtlLocales.includes(currentLocale.value))

const changeLanguage = (langCode: string) => {
  locale.value = langCode
  localStorage.setItem('locale', langCode)
  isOpen.value = false
}
</script>

<template>
  <div ref="dropdownRef" class="relative">
    <button
      v-if="languages.length > 1 && !loading"
      @click="isOpen = !isOpen"
      class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
      :class="{ 'border-none': borderless, 'btn! btn-status-default! px-3!': !showArrow }"
    >
      <span>{{ currentLanguage?.flag }}</span>
      <span>{{ showArrow ? currentLanguage?.code.toUpperCase() : currentLanguage?.name }}</span>
      <svg
        v-if="showArrow"
        class="w-4 h-4 transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div
      v-if="isOpen"
      class="absolute mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 max-h-64 overflow-y-auto"
      :class="isRTL ? 'left-0' : 'right-0'"
    >
      <button
        v-for="lang in languages"
        :key="lang.code"
        @click="changeLanguage(lang.code)"
        :class="[
          'flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors cursor-pointer',
          { 'bg-blue-50 text-blue-700': currentLocale === lang.code },
        ]"
      >
        <span>{{ lang.flag }}</span>
        <span>{{ lang.name }}</span>
        <svg
          v-if="currentLocale === lang.code"
          class="w-4 h-4 ml-auto"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fill-rule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
    </div>
  </div>
</template>
